import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { PaymentWizardDialogComponent } from '@app/account/finance-page/components/payment-wizard-dialog/payment-wizard-dialog.component';
import { MeetingFormWrapperModule } from '@app/core/components/meeting-form-wrapper/meeting-form-wrapper.module';
import { EventActionEnum, EventCategoryEnum } from '@app/core/services/analytics';
import { remoteConfigDefaults, RemoteConfigType } from '@app/core/services/remote-config/constants';
import { RemoteConfigService } from '@app/core/services/remote-config/remote-config.service';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { FilterContentComponent } from '@app/library/panes/filter-content/filter-content.component';
import { AbstractContentPane } from '@app/library/panes/models/abstract-content-pane';
import { ScheduleMiniComponent } from '@app/library/panes/my-classes-schedule/schedule-mini/schedule-mini.component';
import { PaginatorModule } from '@app/shared';
import { ChapterComponent } from '@core/components/chapter/chapter.component';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { logger } from '@core/helpers/logger';
import { AccountService, AuthService, BillingService, ClassroomsService, FeaturesService } from '@core/services';
import { WebsocketService } from '@core/websocket';
import { IEntityUpdate } from '@models/lingo2-models/messaging';

import { IconComponent } from '@shared/icon/icon.component';
import { SkeletonComponent } from '@shared/skeleton/skeleton.component';
import { loadMe } from '@store/actions/profile.actions';
import { getMe, getMyBillingPlan } from '@store/reducers/profile.reducer';
import { WebEvent } from 'lingo2-api-models';
import {
  AccountPlan,
  BillingSubscriptionStatusEnum,
  EDashboardMeetingGroup,
  FindMeetingsForMyClassesEvents,
  Meeting,
  TenantEnum,
  User,
} from 'lingo2-models';
import { Router as ContentRouter } from 'lingo2-models/dist/content/router';
import { OnUiButtonModule } from 'onclass-ui';
import { debounceTime, filter, takeUntil } from 'rxjs';

/*
 */
const emptyProps: Partial<EmptyProps> = {
  title: 'my-classes.no-events.title',
  text: 'my-classes.no-events.text',
  link: '/classes',
  link_label: 'my-classes.no-events.link_label',
  link2: '/my-classes/events',
  image: 'schedule',
};

const emptyPropsTeacher: Partial<EmptyProps> = {
  title: 'my-classes.no-events.title',
  text: 'my-classes.no-events.text_teacher',
  action: true,
  link_label: 'my-classes.no-events.link_label_teacher',
  link2: '/my-classes/events',
  image: 'schedule',
};

@Component({
  selector: 'app-my-classes-schedule',
  standalone: true,
  imports: [
    LocaleSharedModule,
    ChapterComponent,
    CommonModule,
    EmptyComponent,
    FilterContentComponent,
    MeetingFormWrapperModule,
    MobileModalModule,
    ModalModule,
    NoContentStubComponent,
    OnUiButtonModule,
    PaginatorModule,
    PaymentWizardDialogComponent,
    RouterModule,
    ScheduleMiniComponent,
    SkeletonComponent,
    SpinnersModule,

    IconComponent,
  ],
  templateUrl: './my-classes-schedule.component.html',
  styleUrls: ['./my-classes-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyClassesScheduleComponent extends AbstractContentPane implements OnInit {
  override _TAG = `[* MYCLASSES] ${this.constructor.name}:`;

  private authService = inject(AuthService);
  private billingService = inject(BillingService);
  private remoteConfigService = inject(RemoteConfigService);
  private websocketService = inject(WebsocketService);
  private features = inject(FeaturesService);
  protected router = inject(Router);

  public meetingJoinRoute = ContentRouter.meetingJoinRoute;
  public classroomRoute = ClassroomsService.classroomRoute;
  public categoryTabs = [
    { id: -1, code: 'all', title: 'my-classes.no-events.filter.All' },
    { id: 1, code: EDashboardMeetingGroup.upcoming, title: 'my-classes.no-events.filter.Upcoming', count: 3 },
    { id: 2, code: EDashboardMeetingGroup.unconfirmed, title: 'my-classes.no-events.filter.Unconfirmed', count: 0 },
    { id: 3, code: EDashboardMeetingGroup.past, title: 'my-classes.no-events.filter.Past', count: 2 },
    { id: 4, code: EDashboardMeetingGroup.canceled, title: 'my-classes.no-events.filter.Canceled' },
  ];
  public selectedTab: any = { id: -1, code: 'all', title: 'All' };
  public meetingFormOpened = false;
  // public events: Meeting[];
  public isGuest: boolean;
  public isTeacher: boolean;
  public showEmptyContent = false;
  public emptyContent: Partial<EmptyProps>;
  public showSkeleton = true;
  public plan: AccountPlan;
  public openResumePlanModal: boolean;
  public me: User;
  public contentLoaded = false;

  private meId: string;
  private remoteConfig: RemoteConfigType;

  /*
   */
  public ngOnInit() {
    console.debug(`${this._TAG} ngOnInit()`);
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.isGuest = AccountService.isGuest(me);
          this.isTeacher = AccountService.isAsIfTeacher(me);
          if (this.isMeVersion()) {
            emptyProps.link_label = null;
          }
          this.emptyContent = this.isTeacher ? emptyPropsTeacher : emptyProps;
          this.detectChanges();
        },
        error: (error) => {
          this.store.dispatch(loadMe());
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    this.remoteConfigService.config$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (config) => {
        this.remoteConfig = config;
        this.detectChanges();
      },
      error: (error) => {
        this.remoteConfig = remoteConfigDefaults;
        this.detectChanges();
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.store
      .select(getMyBillingPlan)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (plan) => {
          this.plan = plan;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.emptyContent = this.isTeacher ? emptyPropsTeacher : emptyProps;
    this.initRefresh();
    this.loadMeetings();
  }

  hasContent(): boolean {
    // prevent super.hasContent();
    return true;
  }

  protected loadContent() {
    // prevent super.loadContent();
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  protected onContentLoaded() {
    console.debug(`${this._TAG} onContentLoaded(): `, this.filteredEvents);

    this.isVisible = true;
    this.showEmptyContent = !this.filteredEvents.length && this.pane.display_mode !== 'row';
    this.showSkeleton = false;
    this.contentLoaded = true;
  }

  public toggleResumePlan() {
    this.openResumePlanModal = !this.openResumePlanModal;
    this.detectChanges();
  }

  public onCheckScheduleMeeting() {
    if (!this.isTeacher) {
      return;
    }
    if (this.plan?.subscription_status !== BillingSubscriptionStatusEnum.not_active) {
      this.onScheduleMeeting();
    } else {
      this.openResumePlanModal = true;
    }
  }

  public onScheduleMeeting() {
    const options = {
      caller: 'app-my-classes-schedule',
      reason: 'schedule meeting',
    };

    this.analytics.event(EventActionEnum.meeting_scheduling, EventCategoryEnum.service, options.caller);

    const _continue = () => {
      if (this.remoteConfig?.scheduleMeeting__paywall) {
        // показать выбор тарифа, а если тариф есть, то отправить на целевую страницу
        this.billingService.showPaywall(() => this.openMeetingForm(), options);
      } else {
        // сразу отправить на целевую страницу
        this.openMeetingForm();
      }
    };

    if (this.remoteConfig?.scheduleMeeting__auth) {
      // показать диалог авторизации, а после авторизации продолжить
      this.authService.showAuthModal(() => _continue(), options);
    } else {
      // продолжить даже если не авторизован
      _continue();
    }
  }

  public openMeetingForm() {
    this.meetingFormOpened = true;
    this.detectChanges();
  }

  public closeMeetingForm() {
    this.meetingFormOpened = false;
    this.detectChanges();
  }

  public onSelectItem(item, force = false): void {
    if (this.selectedTab.id === item.id && !force) {
      return;
    }
    this._pane.pagination.page = 1;
    this.selectedTab = item;
    this.loadMeetings();
  }

  public onChanged() {
    this.loadMeetings();
  }

  public onClick(e): void {
    logger.log(e);
  }

  public get filteredEvents(): Meeting[] {
    if (this.pane.display_mode !== 'row') {
      return (this._pane.contentItems || []).slice(0, 3);
    }
    return this._pane.contentItems || [];
  }

  public trackMeetingByFn(index, item: Meeting): string {
    return item.id;
  }

  load() {
    // prevent super.loadPane()
  }

  protected loadMeetings() {
    if (this._pane.loading) {
      return;
    }

    const filterValues = new FindMeetingsForMyClassesEvents();
    if (this.selectedTab.id !== -1) {
      filterValues.group = this.selectedTab.code;
    }
    this._pane.meetingFilter = filterValues;
    this._pane.loaded = false;
    this.loadPane();
    super.loadMeetings();
    /** @see onContentLoaded() */
  }

  protected initRefresh() {
    /** уведомление об изменении расписания пользователя */
    this.websocketService
      .on<IEntityUpdate>(WebEvent.Update)
      .pipe(
        filter((update) => update.entity === 'schedule' && update.id === this.meId),
        debounceTime(1000),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.loadMeetings();
          this.loadPane();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    /** Реконнект к веб-сокетам */
    this.websocketService.status$
      .pipe(debounceTime(1000))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (isOnline) => {
          if (isOnline) {
            this.watchScheduleRefresh(this.meId);
            this.loadMeetings();
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    /** Смена пользователя */
    this.authService.user$
      .pipe(filter((me) => !!me && me.id !== this.meId))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.meId = me.id;
          this.watchScheduleRefresh(me.id, this.meId);
          this.loadMeetings();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /** Настроить обновление расписания по сообщениям из веб-сокетов */
  private watchScheduleRefresh(me_id: string, old_me_id?: string) {
    if (old_me_id) {
      this.websocketService.stopWatching('schedule', old_me_id);
    }
    if (me_id) {
      this.websocketService.startWatching('schedule', me_id);
    }
  }

  public loadPage(page: number) {
    this._pane.pagination.page = page;
    this.loadMeetings();
  }
}
